import React, { useState, useMemo, useEffect } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import { Card, CardBody, Table, Input } from "reactstrap"
import Loader from "components/Loader/loader"
import { axiosbaseurl } from "config"
import moment from "moment"

const NotificationsPage = () => {
  document.title = "Notifications | Siddhi Tax Consultant"

  const [loading, setLoading] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [searchString, setSearchString] = useState("")
  const [pageSize, setPageSize] = useState(20)
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    fetchNotifications()
  }, [])

  const fetchNotifications = async () => {
    try {
      setLoading(true)
      const res = await axiosbaseurl.get("/notifications")
      if (res.data?.success) {
        setNotifications(res.data.data.reverse() || [])
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const filteredData = useMemo(() => {
    return notifications.filter(
      item =>
        (item.action || "").toLowerCase().includes(searchString.toLowerCase()) ||
        String(item.customerId || "").includes(searchString) ||
        moment(item.createdAt).format("DD-MM-YYYY").includes(searchString)
    )
  }, [notifications, searchString])

  const totalPages = Math.ceil(filteredData.length / pageSize)
  const start = (currentPage - 1) * pageSize

  const paginatedData = useMemo(
    () => filteredData.slice(start, start + pageSize),
    [filteredData, currentPage, pageSize]
  )

  return (
    <div className="page-content">
      {loading && <Loader />}
      <Breadcrumb title="Dashboard" breadcrumbItem="Notifications" />

      <Card className="shadow-lg">
        <CardBody>
          {/* Top controls */}
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
            <div className="d-flex gap-3 flex-wrap align-items-center">
              <Input
                type="select"
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value))
                  setCurrentPage(1)
                }}
                className="rounded-4 w-auto pe-5"
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>

              <Input
                className="rounded-4"
                placeholder="Search by action, customer ID or date..."
                value={searchString}
                onChange={e => {
                  setSearchString(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </div>
          </div>

          {/* Table */}
          <Table bordered responsive>
            <thead className="table-dark">
              <tr>
                <th>#</th>
                {/* <th>Customer ID</th> */}
                <th>Action</th>
                <th>Created At</th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="3" className="text-center">
                    No notifications found
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr key={item.id}>
                    <td>{start + index + 1}</td>
                    {/* <td>{item.customerId || "—"}</td> */}
                    <td>{item.action || "—"}</td>
                    <td>
                      {moment(item.createdAt).format("DD-MM-YYYY")}
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        {moment(item.createdAt).format("h:mm a")}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <nav
            aria-label="Page navigation"
            className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2"
          >
            <h5 className="mb-0">
              Showing {filteredData.length === 0 ? 0 : start + 1} to{" "}
              {Math.min(start + pageSize, filteredData.length)} of{" "}
              {filteredData.length} entries
            </h5>
            <ul
              className="pagination mb-0 flex-wrap justify-content-end"
              style={{ rowGap: 4, maxWidth: "100%" }}
            >
              <li
                className={`page-item ${currentPage === 1 || totalPages === 0 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1
                if (
                  totalPages <= 7 ||
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                ) {
                  return (
                    <li
                      key={pageNum}
                      className={`page-item ${pageNum === currentPage ? "active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  )
                }
                if (
                  pageNum === currentPage - 3 ||
                  pageNum === currentPage + 3
                ) {
                  return (
                    <li key={pageNum} className="page-item disabled">
                      <span className="page-link">…</span>
                    </li>
                  )
                }
                return null
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages || totalPages === 0
                    ? "disabled"
                    : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </CardBody>
      </Card>
    </div>
  )
}

export default NotificationsPage
